import { parseDate } from '@amtrav/dates';
import { CHANGE_OPTION } from '@amtrav/change-trip';
import { getChangeTripActions } from '@amtrav/webservice';

export const COMPONENT_TYPE = {
  F: 'Flight',
  H: 'Hotel',
  C: 'Car',
};

const processCarChange = (c) => {
  const isCancel = c.cancel !== null;
  const driverName = c.componentDescriptions[0]?.currentCar?.displayName;
  const newSegmentData = c?.change?.car?.newSegment;
  const isPersonal = Boolean(c?.change?.car?.shopRequest?.isPersonal);
  const shopRequest = isCancel ? null : { ...c.change.car.shopRequest, isPersonal };

  let requestedChanges = null;
  if (!isCancel) {
    const { request } = c.change.car;
    requestedChanges = {
      carTypeCode: request.carType,
      carTypeLabel: request.carTypeLabel,
      driverName,
      dropOffDate: parseDate(request.dropOff),
      dropOffLocationName: request.dropOffLocationName,
      pickUpDate: parseDate(request.pickUp),
      pickUpLocationName: request.pickUpLocationName,
      vendorName: request.vendorName,
      vendorCode: request.vendorCode,
      termsAndConditions: null,
      pricing: null,
    };
  }
  const { currentCar } = c.componentDescriptions[0];
  const current = currentCar
    ? {
        carTypeCode: currentCar.carTypeCode,
        carTypeLabel: currentCar.carType,
        driverName,
        dropOffDate: parseDate(currentCar.dropOff),
        dropOffLocationName: currentCar.dropOffLocationName,
        pickUpDate: parseDate(currentCar.pickUp),
        pickUpLocationName: currentCar.pickUpLocationName,
        vendorName: currentCar.vendorName,
        vendorCode: currentCar.vendorCode,
        termsAndConditions: currentCar.termsAndConditions,
        pricing: currentCar.pricing,
      }
    : {};
  const newSegment =
    isCancel || !newSegmentData
      ? null
      : {
          carTypeCode: newSegmentData.carTypeCode,
          carTypeLabel: newSegmentData.carType,
          driverName,
          dropOffDate: parseDate(newSegmentData.dropOff),
          dropOffLocationName: newSegmentData.dropOffLocationName || newSegmentData.pickUpLocationName,
          pickUpDate: parseDate(newSegmentData.pickUp),
          pickUpLocationName: newSegmentData.pickUpLocationName,
          vendorName: newSegmentData.vendorName,
          vendorCode: newSegmentData.vendorCode,
          termsAndConditions: newSegmentData.termsAndConditions || null,
          pricing: newSegmentData.pricing,
          creditCard:
            newSegmentData.fop !== undefined && newSegmentData.fop?.creditCard
              ? {
                  cardType: newSegmentData.fop?.creditCard?.cardType,
                  cardNumber: newSegmentData.fop?.creditCard?.cardNum,
                  cardHolderName: newSegmentData.fop?.creditCard?.cardholder,
                  creditCardDisplayAs: newSegmentData.fop?.creditCard?.displayAs,
                  fopType: newSegmentData.fop?.fopType,
                  priceAmount: c.priceAmount,
                  priceLabel: c.priceLabel,
                  hidePricing: c.hidePricing,
                }
              : null,

          // use this to test CreditCardDisplay since we rarely
          // get that data from the back-end for car changes
          // {
          //   cardType: '',
          //   cardNumber: '4xxxxxxxxxxx1111',
          //   cardHolderName: 'Irwin Fletcher',
          //   creditCardDisplayAs: 'Visa *1111 (Irwin Fletcher)',
          //   fopType: 'C',
          //   priceAmount: '$225.18',
          //   priceLabel: 'TBD',
          //   hidePricing: true,
          // },
        };
  return {
    componentId: c.componentId,
    exchangeId: c.exchangeId,
    oneStep: Boolean(c?.change?.oneStep),
    isCancel,
    isPersonal,
    cancel: c.cancel,
    agreedToTerms: Boolean(c.agreedToTerms),
    componentType: COMPONENT_TYPE[c.componentType],
    travelers: [{ displayName: driverName }],
    changeOption: c.cancel === null ? CHANGE_OPTION.modify : CHANGE_OPTION.cancel,
    current,
    requestedChanges,
    newSegment,
    shopRequest,
    changeRules: null,
    cancelRules: null,
    termsMessage: c.termsMessage,
    outOfPolicy: c.outOfPolicy,
    outOfPolicyDetails: c.outOfPolicyDetails,
    needsApproval: c.needsApproval,
    footnoteMessage: c.footnoteMessage,
    hidePricing: c.hidePricing,
  };
};

const processHotelChange = (c) => {
  const isCancel = c.cancel !== null;
  const guestName = c.componentDescriptions[0]?.currentHotel?.displayName;
  const newSegmentData = c?.change?.hotel?.newSegment;
  const isPersonal = Boolean(c?.change?.hotel?.shopRequest?.isPersonal);
  const shopRequest = isCancel ? null : { ...c.change.hotel.shopRequest, isPersonal };
  let requestedChanges = null;
  if (!isCancel) {
    const { request } = c.change.hotel;
    requestedChanges = {
      checkInDate: parseDate(request.checkIn),
      checkOutDate: parseDate(request.checkOut),
      guestName,
      locId: request.locId,
      termsAndConditions: null,
      pricing: null,
    };
  }
  const { currentHotel } = c.componentDescriptions[0];
  const current = currentHotel
    ? {
        checkInDate: parseDate(currentHotel.arrives),
        checkOutDate: parseDate(currentHotel.departs),
        hotelName: currentHotel.hotelName,
        guestName,
        roomType: currentHotel.roomDescription,
        thumbnail: currentHotel.thumbNail,
        hotelAddress: currentHotel.hotelAddress,
        termsAndConditions: currentHotel.rules?.termsAndConditions || null,
        pricing: currentHotel.pricing,
        propId: currentHotel.propId,
      }
    : {};
  const newSegment =
    isCancel || !newSegmentData
      ? null
      : {
          checkInDate: parseDate(newSegmentData.arrives),
          checkOutDate: parseDate(newSegmentData.departs),
          hotelName: newSegmentData.hotelName,
          guestName,
          roomType: newSegmentData.roomDescription,
          thumbnail: newSegmentData.thumbNail,
          hotelAddress: newSegmentData.hotelAddress,
          termsAndConditions: newSegmentData.rules?.termsAndConditions || null,
          pricing: newSegmentData.pricing,
          creditCard:
            newSegmentData.fop !== undefined && newSegmentData.fop?.creditCard
              ? {
                  cardType: newSegmentData.fop?.creditCard?.cardType,
                  cardNumber: newSegmentData.fop?.creditCard?.cardNum,
                  cardHolderName: newSegmentData.fop?.creditCard?.cardholder,
                  creditCardDisplayAs: newSegmentData.fop?.creditCard?.displayAs,
                  fopType: newSegmentData.fop?.fopType,
                  priceAmount: c.priceAmount,
                  priceLabel: c.priceLabel,
                  hidePricing: c.hidePricing,
                }
              : null,
        };
  return {
    componentId: c.componentId,
    exchangeId: c.exchangeId,
    isCancel,
    isPersonal,
    oneStep: Boolean(c?.change?.oneStep),
    cancel: c.cancel,
    agreedToTerms: Boolean(c.agreedToTerms),
    componentType: COMPONENT_TYPE[c.componentType],
    travelers: [{ displayName: guestName }],
    changeOption: c.cancel === null ? CHANGE_OPTION.modify : CHANGE_OPTION.cancel,
    current,
    requestedChanges,
    newSegment,
    shopRequest,
    changeRules: null,
    cancelRules: null,
    termsMessage: c.termsMessage,
    outOfPolicy: c.outOfPolicy,
    outOfPolicyDetails: c.outOfPolicyDetails,
    needsApproval: c.needsApproval,
    footnoteMessage: c.footnoteMessage,
    hidePricing: c.hidePricing,
  };
};

const processFlightChange = (c) => {
  const isCancel = c.cancel !== null;
  const travelersArr = [];
  const newFlightsData = c?.change?.flight?.newFlights;
  const newFlightPrice = c?.change?.flight?.expectedAddCollectGross;
  const newFlightCredit = c?.change?.flight?.residualCredit;
  const isPersonal = Boolean(c?.change?.flight?.shopRequest?.isPersonal);
  const shopRequest = isCancel ? null : { ...c.change.flight.shopRequest, isPersonal };
  let requestedChanges = null;
  if (!isCancel) {
    requestedChanges = c.change.flight.requests.map((rc, i) => ({
      departDate: parseDate(rc.departDate),
      departTimeLabel: rc.departTimeLabel,
      fromCode: rc.fromCode,
      fromName: rc.fromName,
      toCode: rc.toCode,
      toName: rc.toName,
      cabinLabel: rc.cabinTypeLabel,
      cabin: { label: rc.cabinTypeLabel },
      existingFlightToKeep: rc.existingFlightToKeep,
      uniqueId: `${i}-${rc.departDate}-${rc.fromLocId}-${rc.toLocId}`,
    }));
  }
  const hasCurrentFlights = c.componentDescriptions.length > 0;
  let currentFlights = null;
  if (hasCurrentFlights) {
    currentFlights = c.componentDescriptions.map((des) => ({
      departDate: parseDate(des.currentFlight.departs),
      arriveDate: parseDate(des.currentFlight.arrives),
      airlineCode: des.currentFlight.airlineCode,
      airlineName: des.currentFlight.airlineName,
      fromCode: des.currentFlight.fromCode,
      fromName: des.currentFlight.fromName,
      toCode: des.currentFlight.toCode,
      toName: des.currentFlight.toName,
      flightNumber: des.routeAirlineFlightNumbers,
      numStops: des.numStops,
      cabin: { label: des.currentFlight.segments[0].cabinText },
      componentId: des.currentFlight.componentId,
      termsAndConditions: null,
    }));
  }
  let newFlights = null;
  if (!isCancel && newFlightsData) {
    newFlights = newFlightsData.map((nf) => ({
      departDate: parseDate(nf.departs),
      arriveDate: parseDate(nf.arrives),
      airlineCode: nf.airlineCode,
      airlineName: nf.airlineName,
      fromCode: nf.fromCode,
      fromName: nf.fromName,
      toCode: nf.toCode,
      toName: nf.toName,
      flightNumber: nf.routeAirlineFlightNumbers,
      numStops: nf.numStops,
      cabin: { label: nf.segments[0].cabinText },
      componentId: nf.componentId,
      termsAndConditions: null,
    }));
  }
  if (hasCurrentFlights) {
    c.componentDescriptions[0].currentFlight.travelers.forEach((t) => {
      travelersArr.push({
        displayName: t.name,
        nameNum: t.nameNum,
      });
    });
  }
  return {
    componentId: c.componentId,
    exchangeId: c.exchangeId,
    isCancel,
    isPersonal,
    oneStep: Boolean(c?.change?.oneStep),
    cancel: c.cancel,
    agreedToTerms: Boolean(c.agreedToTerms),
    componentType: COMPONENT_TYPE[c.componentType],
    travelers: travelersArr,
    changeOption: c.cancel === null ? CHANGE_OPTION.modify : CHANGE_OPTION.cancel,
    currentFlights,
    requestedChanges,
    newFlights,
    newFlightPrice,
    newFlightCredit,
    shopRequest,
    changeRules: null,
    cancelRules: null,
    creditCard:
      newFlightsData && c?.change?.flight.fop !== undefined && c?.change?.flight.fop?.creditCard
        ? {
            cardType: c?.change?.flight.fop?.creditCard?.cardType,
            cardNumber: c?.change?.flight.fop?.creditCard?.cardNum,
            cardHolderName: c?.change?.flight.fop?.creditCard?.cardholder,
            creditCardDisplayAs: c?.change?.flight.fop?.creditCard?.displayAs,
            fopType: c?.change?.flight.fop?.fopType,
            priceAmount: c.priceAmount,
            priceLabel: c.priceLabel,
            hidePricing: false,
          }
        : null,
    termsMessage: c.termsMessage,
    outOfPolicy: c.outOfPolicy,
    outOfPolicyDetails: c.outOfPolicyDetails,
    needsApproval: c.needsApproval,
    footnoteMessage: c.footnoteMessage,
    hidePricing: false,
  };
};

export const processAutomatedActions = (automatedActions) => {
  const actions = automatedActions.map((ac) => {
    switch (ac.componentType) {
      case 'C':
        return processCarChange(ac);
      case 'H':
        return processHotelChange(ac);
      case 'F':
        return processFlightChange(ac);
      default:
        console.warn(`Unknown component type: ${ac.componentType}`);
        return {};
    }
  });
  return actions;
};

export const getAndProcessAutomatedActions = async (changeId) => {
  const result = await getChangeTripActions(changeId);
  if (result === null) {
    throw new Error('Invalid ChangeTrip Id');
  }
  const { automatedActions, ...rest } = result;
  return { automatedActions: processAutomatedActions(automatedActions), ...rest };
};
